import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Mail, Megaphone, MessageCircle, Users } from "lucide-react";

const ROLES = [
  {
    title: "Social Media Marketing",
    description: "Help us share local business stories, promote upcoming markets, create simple campaign ideas, and keep the 614 Shop Local community active online.",
    details: [
      "Brainstorm and schedule social posts",
      "Highlight businesses, markets, and community updates",
      "Help shape a consistent voice for 614 Shop Local",
      "Share ideas for reels, stories, graphics, and launch content",
    ],
  },
  {
    title: "Outreach",
    description: "Help connect with small businesses, market organizers, vendors, and community partners who may want to be listed or support the platform.",
    details: [
      "Reach out to local businesses and markets",
      "Help answer basic questions about joining the platform",
      "Identify community partners and potential collaborators",
      "Support follow-up with businesses from our waitlist",
    ],
  },
];

const JoinTeamPage = () => {
  return (
    <div className="page-fade-in">
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-20 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#42D78A]/10 flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-[#42D78A]" />
          </div>
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Join the Team
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Help Build 614 Shop Local
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            We are currently a volunteer-run team working to support small businesses, markets, and local shoppers.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-start">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
            <div className="w-14 h-14 rounded-2xl bg-[#42D78A]/10 flex items-center justify-center mb-5">
              <Megaphone className="w-7 h-7 text-[#42D78A]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Volunteer with us
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              We are looking for a few thoughtful people who care about local businesses and want to help us grow the platform in a sustainable way. These roles are volunteer-based for now.
            </p>
            <div className="space-y-3 text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#42D78A]" />
                <a href="mailto:hello@614shoplocal.com" className="font-bold text-[#1a1a1a] hover:text-[#42D78A]">
                  hello@614shoplocal.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-[#42D78A]" />
                <span>Or reach out through the contact form.</span>
              </div>
            </div>
            <Link to="/contact">
              <Button className="mt-7 bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Contact Us
              </Button>
            </Link>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Roles we are looking to fill
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {ROLES.map((role, index) => (
                <AccordionItem key={role.title} value={`role-${index}`} className="border-gray-100">
                  <AccordionTrigger className="text-left text-base font-bold text-[#1a1a1a] hover:text-[#42D78A] hover:no-underline" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {role.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-gray-600 leading-relaxed mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {role.description}
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {role.details.map((detail) => (
                        <li key={detail} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#42D78A] flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoinTeamPage;
